// TypeScript declarations for Intercom
declare global {
  interface Window {
    intercomSettings: {
      app_id: string
      hide_default_launcher: boolean
      user_id?: string
      name?: string
      email?: string
      created_at?: number
    }
    Intercom: (...args: any[]) => void
    attachEvent?: (event: string, callback: () => void) => void
  }
}

// Global function to dynamically load Intercom script
declare global {
  interface Window {
    loadIntercomScript?: (appId: string) => Promise<void>
  }
}

export default defineNuxtPlugin(() => {
  // Create Intercom stub function immediately
  const w = window as any

  if (typeof w.Intercom !== 'function') {
    const i = function (...args: any[]) {
      ;(i as any).c(args)
    }
    ;(i as any).q = []
    ;(i as any).c = function (args: any[]) {
      ;(i as any).q.push(args)
    }
    w.Intercom = i
  }

  // Function to dynamically load the Intercom script when needed
  window.loadIntercomScript = async (appId: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Check if script is already loaded
      const existingScript = document.querySelector(`script[src*="widget.intercom.io/widget/${appId}"]`)
      if (existingScript) {
        resolve()
        return
      }

      // Initialize Intercom settings BEFORE loading script to prevent "launcher disabled" message
      // This ensures Intercom sees the correct settings from the moment it loads
      window.intercomSettings = {
        app_id: appId,
        hide_default_launcher: false // Set to false immediately to prevent disabled warning
      }

      // Load the Intercom widget script
      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.async = true
      script.src = `https://widget.intercom.io/widget/${appId}`

      const loadTimeout = setTimeout(() => {
        reject(new Error('Intercom script loading timed out'))
      }, 10000) // 10 second timeout for script loading

      script.onload = () => {
        clearTimeout(loadTimeout)
        resolve()
      }

      script.onerror = () => {
        clearTimeout(loadTimeout)
        reject(new Error('Failed to load Intercom script'))
      }

      document.head.appendChild(script)
    })
  }
})
